<script lang="ts">
  /**
   * The end of a match, on the shell's `ServerOverlay` laid over the boards
   * (`docs/decisions/0054`): what was played, who won, the final score as the
   * card's figure, the seats' own scores side by side (your own ringed), and
   * the two things left to do.
   *
   * Shown for as long as the server says the match is over; a rematch takes
   * it away. Like every overlay on the duel screen it is the server's: Back,
   * Escape and a press beside it never close it. Back is reported
   * (`onBackAttempt`) and the room decides (after a match it simply leaves).
   */
  import { ServerOverlay } from '@artibens/app-shell';
  import type { SheetBackAttempt } from '@artibens/app-shell';

  interface Tile {
    readonly name: string;
    readonly score: string;
    readonly mine: boolean;
  }

  interface Props {
    /** The variant and its configuration: "Duel Sprint · Hard · 10 lines". */
    readonly eyebrow: string;
    readonly title: string;
    /** One tile per seat in a duel; one for the team in co-op. */
    readonly tiles: readonly Tile[];
    /** The final score, or the lines a co-op run reached. */
    readonly score: string;
    /** A player may ask for a rematch; a spectator may not. */
    readonly canRematch: boolean;
    readonly onRematch: () => void;
    readonly onLeave: () => void;
    readonly onBackAttempt: (reason: SheetBackAttempt) => void;
  }
  const { eyebrow, title, tiles, score, canRematch, onRematch, onLeave, onBackAttempt }: Props =
    $props();
</script>

<ServerOverlay
  open
  {eyebrow}
  {title}
  figure={score}
  primary={canRematch
    ? { label: 'Rematch', onclick: onRematch, testid: 'duel-rematch' }
    : undefined}
  secondary={{ label: 'Leave room', onclick: onLeave, testid: 'duel-end-leave' }}
  onbackattempt={(reason) => {
    if (reason === 'back') onBackAttempt(reason);
  }}
  testid="duel-match-end"
  testids={{ title: 'duel-match-end-title' }}
  figureTestid="duel-final-score"
>
  <div class="tiles" class:tiles--one={tiles.length === 1}>
    {#each tiles as tile, index (index)}
      <div class="tile" class:tile--mine={tile.mine}>
        <span class="tile-who">{tile.name}</span>
        <span class="tile-score">{tile.score}</span>
      </div>
    {/each}
  </div>
</ServerOverlay>

<style>
  .tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--ui-space-2);
  }

  .tiles--one {
    grid-template-columns: 1fr;
  }

  /* A seat's score on the card: a block on the sheet, your own ringed in the
     accent. */
  .tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--ui-space-1);
    min-width: 0;
    padding: var(--ui-space-3) var(--ui-space-2);
    border-radius: var(--ui-radius-md);
    background: var(--ui-surface-2);
  }

  .tile--mine {
    box-shadow: inset 0 0 0 2px var(--ui-accent);
  }

  .tile-who {
    max-width: 100%;
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
    color: var(--ui-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tile-score {
    font: var(--ui-text-display);
    font-variant-numeric: tabular-nums;
    color: var(--ui-ink);
  }
</style>
